import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { IElement } from '@shared/models/project.model';
import { ConcretePropertyName } from '@shared/templates/categories/concrete/concrete.model';
import { useCategoryPropertyPicker } from './use-category-property-picker.hook';
import { GenericProductCategorySelect } from './GenericProductCategorySelect';
import { CategoryPickerPropertyField } from './CategoryPickerPropertyField';
import { IProduct } from '@shared/models/product.model';
import { Box, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import { ElementPropertyName } from '@shared/models/element-property.model';
import {
  getElementProperties,
  isElementExpressionProperty,
} from '@shared/helpers/element-property.helpers';
import type { GenericProductPickerChange } from '@shared/helpers/generic-product-picker.helpers';

/**
 * Properties to exclude from the generic product settings.
 * These are properties that applies to all elements and therefore not exclusive to the generic product
 * */
const EXCLUDE_PROPERTIES = [
  ElementPropertyName.ReusedContent,
  ConcretePropertyName.ReinforcementSteel,
  ConcretePropertyName.StainlessReinforcement,
  ElementPropertyName.SbefCode,
  ElementPropertyName.Lifetime,
] as const;

export type IGenericProductChange = GenericProductPickerChange;

interface GenericProductSelectorProps {
  /**
   * When opening from the EPD menu, provide the generic product id to get the correct settings
   */
  genericProductId?: string;

  /**
   * Either a product or an element to get defaults from
   */
  defaults?: IElement | IProduct;
  showPrompt?: boolean;
  onProductChange?: (change: IGenericProductChange) => void;
}

const GenericProductSelector: React.FC<GenericProductSelectorProps> = ({
  genericProductId,
  defaults,
  showPrompt,
  onProductChange,
}) => {
  const excludePropertyNames = useMemo(() => {
    const expressionPropertyNames =
      defaults && 'properties' in defaults
        ? getElementProperties(defaults)
            .filter(isElementExpressionProperty)
            .map((p) => p.name)
        : [];
    return [...EXCLUDE_PROPERTIES, ...expressionPropertyNames];
  }, [defaults]);

  const { state, visibleProperties, setCategoryId, setPropertyChange, change } =
    useCategoryPropertyPicker({
      genericProductId,
      defaults,
      excludePropertyNames,
    });

  const prevChangeRef = useRef<IGenericProductChange | undefined>(undefined);

  const emit = useCallback(() => {
    if (!onProductChange) return;
    if (!isEqual(change, prevChangeRef.current)) {
      onProductChange(change);
      prevChangeRef.current = change;
    }
  }, [change, onProductChange]);

  useEffect(emit, [emit]);

  return (
    <Box>
      <Typography variant="subtitle2">Product Category</Typography>
      <GenericProductCategorySelect
        categoryId={state.category_id}
        onCategoryChange={setCategoryId}
      />
      {state.category_id && visibleProperties.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 2,
          }}
        >
          {visibleProperties.map((property) => (
            <CategoryPickerPropertyField
              key={property.id}
              property={property}
              onPropertyChange={setPropertyChange}
            />
          ))}
        </Box>
      )}
      {showPrompt && !change.product_id && (
        <Typography
          variant="caption"
          color="error"
          sx={{
            pl: 3.5,
          }}
        >
          Generic product not found for the selected category
        </Typography>
      )}
    </Box>
  );
};

export default GenericProductSelector;
